import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { PiShoppingBag } from "react-icons/pi";
import { MdAccessTime } from "react-icons/md";
import { FaRupeeSign } from "react-icons/fa";
import { FiDownloadCloud, FiChevronDown, FiChevronUp } from "react-icons/fi";
import moment from "moment";
import ExcelJS from "exceljs";
import {
    getPartner,
    getPartnerDetails,
    getPartnerView,
    getPayoutDrop,
    getRazorpayTransferStatement,
} from "../../../../Service/partnerAPI";
import Loader from "../../../../reusableUI/loader";
import { topBarImages } from "../../../../reusableUI/images";

/* ---------------- formatting helpers ---------------- */
const formatCurrency = (value) =>
    `₹${(Number(value) || 0).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
    })}`;

const formatDate = (dateStr) =>
    dateStr ? moment(dateStr).format("D MMM, YYYY") : "-";

const formatDateRange = (from, to) => {
    if (!from) return "-";
    if (!to || from === to) return formatDate(from);
    return `${formatDate(from)} - ${formatDate(to)}`;
};

const formatTimeRange = (time) => time || "-";

/* ---------------- ✅ STATUS HELPERS (ONLY ON-GOING + COMPLETED) ---------------- */
const normalizeStatusKey = (status) =>
    String(status || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, "");

const isOngoingStatus = (status) => {
    const s = normalizeStatusKey(status);
    return s === "accepted" || s === "startduty" || s === "ongoing";
};

const isCompletedStatus = (status) => {
    const s = normalizeStatusKey(status);
    return s === "completed" || s === "endduty";
};

const isEligibleVisitStatus = (status) =>
    isOngoingStatus(status) || isCompletedStatus(status);

/* ---------------- payout transaction helpers ---------------- */
const getSettledDutyTransactions = (dutyTransactions = []) =>
    dutyTransactions.filter((dt) => {
        const s = (dt.status || "").toLowerCase();
        return s === "processed" || s === "completed";
    });

const getServicesList = (payout) => {
    if (Array.isArray(payout?.services) && payout.services.length) {
        return Array.from(
            new Set(
                payout.services
                    .filter((x) => typeof x === "string")
                    .map((x) => x.trim())
                    .filter(Boolean)
            )
        );
    }

    const set = new Set();
    (payout?.dutyTransactions || []).forEach((dt) => {
        (dt?.services || []).forEach((s) => {
            if (typeof s === "string" && s.trim()) set.add(s.trim());
            else if (s?.name) set.add(String(s.name).trim());
        });
    });

    return Array.from(set);
};

/* ---------------- UI ---------------- */
const SummaryCard = ({ title, amount, subtitle, icon, accent, sub }) => (
    <div className="bg-white rounded-[12px] shadow-sm border border-slate-100 px-4 py-6 flex items-center justify-between">
        <div className="flex flex-col">
            <p className="text-xs font-medium text-slate-500 font-poppins">{title}</p>
            <p className="mt-1 text-2xl font-semibold text-slate-900 font-poppins">
                {formatCurrency(amount)}
            </p>
            {subtitle ? (
                <p className="mt-1 text-[11px] text-emerald-500 font-poppins">
                    {subtitle}
                </p>
            ) : null}
            {sub !== undefined && sub !== null && sub !== "" && (
                <p className="mt-1 text-[12px] text-slate-400 font-poppins">
                    No. of Payouts:{" "}
                    <span className="font-medium text-slate-600">{sub}</span>
                </p>
            )}
        </div>

        <div
            className={`w-10 h-10 rounded-2xl flex items-center justify-center text-[18px] ${accent}`}
        >
            {icon}
        </div>
    </div>
);

const StatusPill = ({ status }) => {
    const s = normalizeStatusKey(status);

    if (s === "accepted" || s === "startduty" || s === "ongoing") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[12px] text-[11px] font-poppins font-medium bg-blue-50 text-blue-600">
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                On-Going
            </span>
        );
    }

    if (s === "completed" || s === "endduty") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[12px] text-[11px] font-poppins font-medium bg-emerald-50 text-emerald-600">
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                Completed
            </span>
        );
    }

    return null;
};

const DayStatusPill = ({ status }) => {
    const s = (status || "").toLowerCase();
    let text = status || "";
    let cls = "bg-slate-50 text-slate-600 border-slate-100";

    if (s === "processed" || s === "completed") {
        cls = "bg-emerald-50 text-emerald-600 border-emerald-100";
        text = s === "processed" ? "Processed" : "Completed";
    } else if (s === "pending" || s === "payment pending") {
        cls = "bg-amber-50 text-amber-600 border-amber-100";
        text = "Pending";
    }

    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded-[12px] text-[11px] font-medium font-poppins border ${cls}`}
        >
            {text}
        </span>
    );
};

const BreakdownTable = ({ dutyTransactions, totalDays }) => {
    const totalDailyFee = dutyTransactions.reduce(
        (sum, d) => sum + (d.dailyFee || 0),
        0
    );
    const totalPenalty = dutyTransactions.reduce(
        (sum, d) => sum + (d.penalty || 0),
        0
    );
    const totalPlatformFee = 0;

    const totalAmount = dutyTransactions.reduce((sum, d) => {
        if (d.amountToTransfer != null) return sum + d.amountToTransfer;
        return sum + (d.dailyFee || 0) - (d.penalty || 0) + (d.bonus || 0);
    }, 0);

    return (
        <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[920px] text-[11px] sm:text-[12px] bg-white rounded-[6px] overflow-hidden text-center">
                <thead className="bg-slate-100">
                    <tr className="text-slate-500 font-poppins">
                        <th className="py-2 px-3 font-medium text-center">Day</th>
                        <th className="py-2 px-3 font-medium text-center">Date</th>
                        <th className="py-2 px-3 font-medium text-center">Attended By</th>
                        <th className="py-2 px-3 font-medium text-center">Daily Fee</th>
                        <th className="py-2 px-3 font-medium text-center">Penalty</th>
                        <th className="py-2 px-3 font-medium text-center">Bonus</th>
                        <th className="py-2 px-3 font-medium text-center">Status</th>
                        <th className="py-2 px-3 font-medium text-center">Amount</th>
                    </tr>
                </thead>

                <tbody>
                    {dutyTransactions.map((d, idx) => {
                        const amount =
                            d.amountToTransfer != null
                                ? d.amountToTransfer
                                : (d.dailyFee || 0) - (d.penalty || 0) + (d.bonus || 0);

                        return (
                            <tr
                                key={idx}
                                className="border-t border-slate-100 font-poppins"
                            >
                                <td className="py-2 px-3 align-top text-center">
                                    <div className="text-[12px] font-medium text-slate-900">
                                        {d.day || `Day ${idx + 1}/${totalDays}`}
                                    </div>
                                </td>

                                <td className="py-2 px-3 align-top text-center text-slate-700">
                                    {d.date
                                        ? moment(d.date, "YYYY-MM-DD").format("D MMM, YYYY")
                                        : "-"}
                                </td>

                                <td className="py-2 px-3 align-top text-center text-slate-700">
                                    {d.attendedBy || "-"}
                                </td>

                                <td className="py-2 px-3 align-top text-center text-slate-700">
                                    {formatCurrency(d.dailyFee)}
                                </td>

                                <td className="py-2 px-3 align-top text-center text-slate-700">
                                    {formatCurrency(d.penalty)}
                                </td>

                                <td className="py-2 px-3 align-top text-center text-slate-700">
                                    {formatCurrency(0)}
                                </td>

                                <td className="py-2 px-3 align-top text-center">
                                    <DayStatusPill status={d.status} />
                                </td>

                                <td className="py-2 px-3 align-top text-center font-semibold text-slate-900">
                                    {formatCurrency(amount)}
                                </td>
                            </tr>
                        );
                    })}

                    <tr className="bg-slate-50 border-t border-slate-200">
                        <td className="py-2 px-3 text-[12px] font-semibold text-slate-900 font-poppins text-center pr-4">
                            <div className="flex flex-col items-center leading-tight">
                                <span>Total</span>
                            </div>
                        </td>
                        <td />
                        <td />
                        <td className="px-3 text-[12px] text-center text-slate-700">
                            {formatCurrency(totalDailyFee)}
                        </td>
                        <td className="px-3 text-[12px] text-center text-slate-700">
                            {formatCurrency(totalPenalty)}
                        </td>
                        <td className="px-3 text-[12px] text-center text-slate-700">
                            {formatCurrency(totalPlatformFee)}
                        </td>
                        <td />
                        <td className="px-3 text-[12px] text-center font-semibold text-slate-900">
                            {formatCurrency(totalAmount)}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

/* ---------------- Payout Card (FIXED) ---------------- */
const PayoutCard = ({ payout, expanded, onToggle, fetchBreakdown }) => {
    const {
        jobID,
        status,
        patientName,
        visitAddress,
        fromDate,
        toDate,
        time,
        dutyTransactions = [],
    } = payout;

    // ✅ Correct visit id selection
    const visitID = payout?.visitID || payout?.visitId || payout?._id || null;

    const [breakdownData, setBreakdownData] = useState(null);
    const [breakdownLoading, setBreakdownLoading] = useState(false);
    const [breakdownError, setBreakdownError] = useState("");
    const [activeTabIdx, setActiveTabIdx] = useState(0);

    // ✅ prevent repeated calls when API fails
    const fetchedOnceRef = useRef(false);

    useEffect(() => {
        // reset only when the payout changes (visitID changes)
        fetchedOnceRef.current = false;
        setBreakdownData(null);
        setBreakdownError("");
        setActiveTabIdx(0);
    }, [visitID]);

    useEffect(() => {
        if (!expanded) return;
        if (!visitID) return;
        if (fetchedOnceRef.current) return;

        fetchedOnceRef.current = true;

        let alive = true;
        const run = async () => {
            try {
                setBreakdownLoading(true);
                setBreakdownError("");

                const bd = await fetchBreakdown(visitID);
                if (!alive) return;

                setBreakdownData(bd);
            } catch (err) {
                if (!alive) return;
                setBreakdownError(err?.message || "Failed to load payout breakdown details.");
            } finally {
                if (!alive) return;
                setBreakdownLoading(false);
            }
        };

        run();

        return () => {
            alive = false;
        };
    }, [expanded, visitID, fetchBreakdown]);

    const settledDutyTransactions = getSettledDutyTransactions(dutyTransactions);

    const computedTotalAmount = settledDutyTransactions.reduce((sum, d) => {
        if (d.amountToTransfer != null) return sum + d.amountToTransfer;
        return sum + (d.dailyFee || 0) - (d.penalty || 0) + (d.bonus || 0);
    }, 0);

    const avatarInitials = (patientName || "")
        .split(" ")
        .filter(Boolean)
        .map((n) => n[0])
        .join("");

    const servicesList = getServicesList(payout);

    return (
        <div className="bg-white rounded-[12px] border border-slate-200 shadow-sm overflow-hidden">
            <div
                className="px-4 py-3 flex flex-col gap-3 cursor-pointer hover:bg-slate-50"
                onClick={onToggle}
            >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            <span className="text-[12px] font-semibold text-slate-900 font-poppins">
                                {jobID}
                            </span>
                            <StatusPill status={status} />
                        </div>

                        <div className="flex items-center gap-2 mt-1">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-[11px] font-semibold text-indigo-700 font-poppins">
                                {avatarInitials}
                            </div>

                            <div className="min-w-0">
                                <p className="text-[12px] font-medium text-slate-900 font-poppins truncate">
                                    {patientName}
                                </p>
                                <p className="text-[11px] text-slate-500 font-poppins truncate">
                                    {visitAddress || "-"}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="w-full md:w-auto md:flex-1 md:max-w-[720px]">
                        <div className="grid grid-cols-2 sm:grid-cols-4 items-start gap-x-8 gap-y-3 text-[11px] text-slate-500">
                            <div className="text-left min-w-0">
                                <p className="font-poppins">Visit Dates</p>
                                <p className="mt-1 text-[12px] font-medium text-slate-900 font-poppins break-words">
                                    {formatDateRange(fromDate, toDate)}
                                </p>
                            </div>

                            <div className="text-left min-w-0">
                                <p className="font-poppins">Time</p>
                                <p className="mt-1 text-[12px] font-medium text-slate-900 font-poppins break-words">
                                    {formatTimeRange(time)}
                                </p>
                            </div>

                            <div className="text-left min-w-0">
                                <p className="font-poppins">Services</p>
                                {servicesList.length ? (
                                    <ul className="mt-1 text-[12px] font-medium text-slate-900 font-poppins list-decimal list-inside space-y-0.5">
                                        {servicesList.map((service, idx) => (
                                            <li
                                                key={`${service}-${idx}`}
                                                className="break-words leading-4"
                                            >
                                                {service}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="mt-1 text-[12px] font-medium text-slate-900 font-poppins">
                                        -
                                    </p>
                                )}
                            </div>

                            <div className="text-left min-w-0">
                                <p className="font-poppins">Total Amount</p>
                                <p className="mt-1 text-[12px] font-medium text-slate-900 font-poppins break-words">
                                    {formatCurrency(computedTotalAmount)}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex md:flex-col items-start md:items-end justify-between md:justify-start gap-2">
                        <div className="flex items-center gap-1 text-[11px] text-indigo-600 font-poppins font-medium select-none">
                            <span>{expanded ? "Hide breakdown" : "View breakdown"}</span>
                            {expanded ? (
                                <FiChevronUp size={12} />
                            ) : (
                                <FiChevronDown size={12} />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-4 pb-3 pt-2 bg-slate-50/60 border-t border-slate-100">
                {expanded && (
                    <div className="mb-4 bg-slate-50 border border-slate-200/60 rounded-[12px] p-4 font-poppins text-left">
                        <h4 className="text-[13px] font-semibold text-slate-800 mb-3 flex items-center gap-2">
                            <span className="w-1.5 h-3.5 bg-blue-600 rounded-full inline-block" />
                            Payout Details
                        </h4>

                        {breakdownLoading && (
                            <div className="py-6 flex items-center justify-center gap-2 text-[12px] text-slate-500">
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-500 border-t-transparent" />
                                Loading breakdown details...
                            </div>
                        )}

                        {breakdownError && (
                            <div className="text-[11px] text-red-500 py-2">
                                {breakdownError}
                            </div>
                        )}

                        {!breakdownLoading && !breakdownError && breakdownData && (() => {
                            const items = Array.isArray(breakdownData)
                                ? breakdownData
                                : breakdownData
                                    ? [{ type: "Payout", values: breakdownData }]
                                    : [];

                            const activeItem = items[activeTabIdx] || items[0];
                            const values = activeItem?.values || {};
                            const gatewayFee =
                                values.gatewayCharges !== undefined
                                    ? values.gatewayCharges
                                    : Math.max(
                                        0,
                                        (values.patientPaid || 0) -
                                        (values.grossHospitalShare || 0) -
                                        (values.ceviliyarPlatFormFee || 0)
                                    );

                            return (
                                <div className="mt-3 bg-slate-50 p-1 font-poppins text-left">
                                    {items.length > 1 && (
                                        <div className="flex flex-wrap gap-1.5 mb-4 bg-slate-200/50 p-1 rounded-xl w-fit border border-slate-200/40">
                                            {items.map((item, idx) => (
                                                <button
                                                    key={idx}
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setActiveTabIdx(idx);
                                                    }}
                                                    className={`px-3.5 py-1.5 rounded-lg text-[11px] font-semibold font-poppins transition-all duration-200 select-none ${activeTabIdx === idx
                                                            ? "bg-white text-indigo-600 shadow-sm"
                                                            : "text-slate-500 hover:text-slate-800 hover:bg-white/40"
                                                        }`}
                                                >
                                                    {item.type || `Session ${idx + 1}`}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    <div className="flex flex-wrap items-stretch gap-3">
                                        <div className="flex-1 min-w-[160px] bg-white p-3 rounded-[8px] border border-slate-200/80 shadow-sm flex flex-col justify-between">
                                            <div>
                                                <p className="font-semibold text-slate-900 text-[11px]">Total Patient Payment</p>
                                                <p className="text-[9px] text-slate-400 leading-normal mt-0.5">Base amount paid by patient</p>
                                            </div>
                                            <span className="font-bold text-slate-900 text-[13px] mt-2 block">
                                                {formatCurrency(values.patientPaid)}
                                            </span>
                                        </div>

                                        <div className="flex-1 min-w-[160px] bg-white p-3 rounded-[8px] border border-slate-200/80 shadow-sm flex flex-col justify-between">
                                            <div>
                                                <p className="font-medium text-slate-700 text-[11px]">Payment Gateway Fee</p>
                                                <p className="text-[9px] text-slate-400 leading-normal mt-0.5">
                                                    charges: {values.gatewayChargesInPercentage || "2% + 18% GST"}
                                                </p>
                                            </div>
                                            <span className="font-semibold text-red-500 text-[13px] mt-2 block">
                                                - {formatCurrency(gatewayFee)}
                                            </span>
                                        </div>

                                        <div className="flex-1 min-w-[160px] bg-white p-3 rounded-[8px] border border-slate-200/80 shadow-sm flex flex-col justify-between">
                                            <div>
                                                <p className="font-medium text-slate-700 text-[11px]">Ceviliyar Platform Fee</p>
                                                <p className="text-[9px] text-slate-400 leading-normal mt-0.5">
                                                    {values.ceviliyarPlatformInPercentage || "5"}% of total payment
                                                </p>
                                            </div>
                                            <span className="font-semibold text-red-500 text-[13px] mt-2 block">
                                                - {formatCurrency(values.ceviliyarPlatFormFee)}
                                            </span>
                                        </div>

                                        <div className="flex-1 min-w-[160px] bg-white p-3 rounded-[8px] border border-slate-200/80 shadow-sm flex flex-col justify-between">
                                            <div>
                                                <p className="font-semibold text-slate-900 text-[11px]">Gross Hospital Share</p>
                                                <p className="text-[9px] text-slate-400 leading-normal mt-0.5">Amount before bank transfer</p>
                                            </div>
                                            <span className="font-bold text-slate-900 text-[13px] mt-2 block">
                                                {formatCurrency(values.grossHospitalShare)}{" "}
                                                <span className="text-[11px] font-normal"> Per day</span>
                                            </span>
                                        </div>

                                        <div className="flex-1 min-w-[160px] bg-white p-3 rounded-[8px] border border-slate-200/80 shadow-sm flex flex-col justify-between">
                                            <div>
                                                <p className="font-medium text-slate-700 text-[11px]">Automated Transfer Fee</p>
                                                <p className="text-[9px] text-slate-400 leading-normal mt-0.5">
                                                    charges: {values.automatedTranferFeeInPercentage || "0.25% + 18% GST"}
                                                </p>
                                            </div>
                                            <span className="font-semibold text-red-500 text-[13px] mt-2 block">
                                                - {formatCurrency(values.automatedTranferFee)}
                                            </span>
                                        </div>

                                        <div className="flex-1 min-w-[160px] bg-gradient-to-br from-blue-50 to-indigo-50/50 p-3 rounded-[8px] border border-blue-100 shadow-sm flex flex-col justify-between">
                                            <div>
                                                <p className="font-bold text-blue-900 text-[11px]">Net Payout to Hospital</p>
                                                <p className="text-[9px] text-blue-600 leading-normal mt-0.5">Credited to bank account</p>
                                            </div>
                                            <span className="font-extrabold text-blue-700 text-[14px] mt-2 block">
                                                {formatCurrency(values.netPayoutToVendor)}{" "}
                                                <span className="text-[11px] font-normal"> Per day</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}

                {expanded && settledDutyTransactions.length > 0 && (
                    <BreakdownTable
                        dutyTransactions={settledDutyTransactions}
                        totalDays={settledDutyTransactions.length}
                    />
                )}

                {expanded && settledDutyTransactions.length === 0 && (
                    <div className="py-3 text-[12px] text-slate-500 font-poppins text-left">
                        No processed/completed payout entries for this duty.
                    </div>
                )}
            </div>
        </div>
    );
};

/* ---------------- Razorpay transfer statement workbook ---------------- */
const TRANSFER_REPORT_COLUMNS = [
    ["Transfer ID", 24],
    ["Status", 12],
    ["Source Payment ID", 22],
    ["Recipient ID", 22],
    ["Amount (INR)", 15],
    ["Amount Reversed (INR)", 19],
    ["Fees (INR)", 13],
    ["Tax (INR)", 12],
    ["Currency", 10],
    ["Settlement Status", 17],
    ["Recipient Settlement ID", 24],
    ["Settlement Batch Amount (INR)", 23],
    ["UTR", 18],
    ["On Hold", 10],
    ["Hold Until (IST)", 19],
    ["Created At (IST)", 19],
    ["Processed At (IST)", 19],
    ["Source Channel", 15],
    ["Error Code", 16],
    ["Error Description", 28],
    ["Notes", 24],
];

const paiseToRupees = (value) => (Number(value) || 0) / 100;
const formatUnixTimeInIST = (value) =>
    value ? moment.unix(Number(value)).utcOffset(330).format("YYYY-MM-DD HH:mm:ss") : null;

const downloadTransferStatementExcel = async ({ report, from, to }) => {
    const transfers = Array.isArray(report?.transfers) ? report.transfers : [];
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "Ceviliyar";
    workbook.created = new Date();

    const worksheet = workbook.addWorksheet("Transfers", {
        views: [{ state: "frozen", ySplit: 7 }],
    });
    const fillRow = (row, color) => {
        for (let column = 1; column <= TRANSFER_REPORT_COLUMNS.length; column += 1) {
            row.getCell(column).fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: color },
            };
        }
    };
    worksheet.columns = TRANSFER_REPORT_COLUMNS.map(([header, width], index) => ({
        header,
        key: `column${index + 1}`,
        width,
    }));

    worksheet.spliceRows(1, 1);
    worksheet.insertRows(1, [[], [], [], [], [], [], []]);
    worksheet.mergeCells("A1:U1");
    worksheet.mergeCells("A2:U2");
    ["A4:D4", "E4:H4", "I4:L4", "M4:P4", "Q4:U4"].forEach((range) =>
        worksheet.mergeCells(range)
    );
    ["A5:D5", "E5:H5", "I5:L5", "M5:P5", "Q5:U5"].forEach((range) =>
        worksheet.mergeCells(range)
    );

    worksheet.getCell("A1").value = "Razorpay Transfer Report";
    const period = from && to
        ? `${from.format("DD MMM YYYY")} - ${to.format("DD MMM YYYY")}`
        : "Overall";
    worksheet.getCell("A2").value = `${report?.partnerName || "Partner"}  |  ${period}  |  ${transfers.length} transfer records  |  Times shown in IST`;

    const totalAmount = transfers.reduce((sum, item) => sum + paiseToRupees(item.amount), 0);
    const totalFees = transfers.reduce((sum, item) => sum + paiseToRupees(item.fees), 0);
    const totalTax = transfers.reduce((sum, item) => sum + paiseToRupees(item.tax), 0);
    const settledTransfers = transfers.filter(
        (item) => String(item.settlementStatus || "").toLowerCase() === "settled"
    ).length;

    [
        ["A4", "TOTAL TRANSFERS", "A5", transfers.length],
        ["E4", "TOTAL AMOUNT", "E5", totalAmount],
        ["I4", "TOTAL FEES", "I5", totalFees],
        ["M4", "TOTAL TAX", "M5", totalTax],
        ["Q4", "SETTLED TRANSFERS", "Q5", settledTransfers],
    ].forEach(([labelCell, label, valueCell, value]) => {
        worksheet.getCell(labelCell).value = label;
        worksheet.getCell(valueCell).value = value;
    });

    TRANSFER_REPORT_COLUMNS.forEach(([header], index) => {
        worksheet.getCell(7, index + 1).value = header;
    });

    transfers.forEach((item) => {
        worksheet.addRow([
            item.id,
            item.status,
            item.source,
            item.recipient,
            paiseToRupees(item.amount),
            paiseToRupees(item.amountReversed),
            paiseToRupees(item.fees),
            paiseToRupees(item.tax),
            item.currency,
            item.settlementStatus,
            item.recipientSettlementID,
            paiseToRupees(item.settlementAmount),
            item.settlementUTR,
            item.onHold,
            formatUnixTimeInIST(item.onHoldUntil),
            formatUnixTimeInIST(item.createdAt),
            formatUnixTimeInIST(item.processedAt),
            item.sourceChannel,
            item.errorCode,
            item.errorDescription,
            Object.keys(item.notes || {}).length ? JSON.stringify(item.notes) : null,
        ]);
    });

    const totalRow = worksheet.addRow([
        "TOTAL", null, null, null,
        totalAmount,
        transfers.reduce((sum, item) => sum + paiseToRupees(item.amountReversed), 0),
        totalFees,
        totalTax,
    ]);
    worksheet.mergeCells(`A${totalRow.number}:D${totalRow.number}`);

    worksheet.getRow(1).height = 34;
    worksheet.getRow(2).height = 22;
    worksheet.getRow(5).height = 30;
    worksheet.getRow(7).height = 32;
    worksheet.getCell("A1").font = { bold: true, size: 18, color: { argb: "FFFFFFFF" } };
    worksheet.getCell("A1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF17324D" } };
    worksheet.getCell("A1").alignment = { vertical: "middle", horizontal: "left" };
    worksheet.getCell("A2").font = { color: { argb: "FF36546F" } };
    worksheet.getCell("A2").fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEAF1F8" } };

    ["A4", "E4", "I4", "M4", "Q4"].forEach((cell) => {
        worksheet.getCell(cell).font = { bold: true, color: { argb: "FF36546F" } };
        worksheet.getCell(cell).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFDCE8F3" } };
    });
    ["A5", "E5", "I5", "M5", "Q5"].forEach((cell) => {
        worksheet.getCell(cell).font = { bold: true, size: 15, color: { argb: "FF17324D" } };
        worksheet.getCell(cell).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF7FAFC" } };
    });
    ["E5", "I5", "M5"].forEach((cell) => {
        worksheet.getCell(cell).numFmt = '₹#,##0.00';
    });

    const headerRow = worksheet.getRow(7);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    fillRow(headerRow, "FF36546F");
    headerRow.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    headerRow.eachCell((cell) => {
        cell.border = { bottom: { style: "thin", color: { argb: "FF17324D" } } };
    });

    for (let rowNumber = 8; rowNumber < totalRow.number; rowNumber += 1) {
        const row = worksheet.getRow(rowNumber);
        row.height = 20;
        if (rowNumber % 2 === 0) {
            fillRow(row, "FFF7FAFC");
        }
        [5, 6, 7, 8, 12].forEach((column) => {
            row.getCell(column).numFmt = '₹#,##0.00';
        });
    }

    totalRow.height = 24;
    totalRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    fillRow(totalRow, "FF17324D");
    [5, 6, 7, 8].forEach((column) => {
        totalRow.getCell(column).numFmt = '₹#,##0.00';
    });
    worksheet.autoFilter = `A7:U${Math.max(7, totalRow.number - 1)}`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `razorpay_transfers_${from ? from.format("YYYYMMDD") : "overall"}_${to ? to.format("YYYYMMDD") : "all"}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

/* ---------------- MAIN COMPONENT ---------------- */
const Payouts = ({ partnerID, metricsData, transactionsData, partnerInfoData }) => {
    const [partnerInfo, setPartnerInfo] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [transactions, setTransactions] = useState([]);

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [timeFilter, setTimeFilter] = useState("all");

    const [expandedId, setExpandedId] = useState(null);

    const [loading, setLoading] = useState(true);

    const [showStatementModal, setShowStatementModal] = useState(false);
    const [statementType, setStatementType] = useState("last10");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);

    const isInjected = metricsData !== undefined && transactionsData !== undefined;

    /* ✅ Cache breakdown per visitID to stop repeated calls */
    const payoutBreakdownCacheRef = useRef(new Map());

    const fetchPayoutBreakdownCached = useCallback(async (visitID) => {
        if (!visitID) throw new Error("Missing visitID");

        if (payoutBreakdownCacheRef.current.has(visitID)) {
            return payoutBreakdownCacheRef.current.get(visitID);
        }

        const res = await getPayoutDrop(visitID);

        // Accept both styles: {error:false,data:...} or axios {data:{error:false,data:...}}
        const raw = res?.data?.data ?? res?.data ?? res;
        const ok = raw?.error === false || raw?.success === true || res?.error === false || res?.success === true;

        const payload = raw?.data ?? raw;
        if (!ok || !payload) {
            throw new Error(raw?.message || res?.message || "Failed to load breakdown");
        }

        payoutBreakdownCacheRef.current.set(visitID, payload);
        return payload;
    }, []);

    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            setLoading(true);
            try {
                if (isInjected) {
                    if (cancelled) return;
                    setPartnerInfo(partnerInfoData ?? null);
                    setMetrics(metricsData ?? null);
                    setTransactions(Array.isArray(transactionsData) ? transactionsData : []);
                    return;
                }

                const resp = partnerID ? await getPartnerView(partnerID) : await getPartner();
                if (cancelled) return;

                if (resp?.error === false && resp?.data?._id) {
                    const partner = resp.data;
                    setPartnerInfo(partner);

                    const detailsRes = await getPartnerDetails(partner._id);
                    if (cancelled) return;

                    if (detailsRes?.error === false && detailsRes?.data) {
                        setMetrics(detailsRes.data.metrics ?? null);
                        setTransactions(
                            Array.isArray(detailsRes.data.transactions) ? detailsRes.data.transactions : []
                        );
                    } else {
                        setMetrics(null);
                        setTransactions([]);
                    }
                } else {
                    setPartnerInfo(null);
                    setMetrics(null);
                    setTransactions([]);
                }
            } catch (err) {
                console.error("Error fetching partner data:", err);
                if (!cancelled) {
                    setPartnerInfo(null);
                    setMetrics(null);
                    setTransactions([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        init();

        return () => {
            cancelled = true;
        };
    }, [partnerID, isInjected, metricsData, transactionsData, partnerInfoData]);

    const totalEarnings = metrics?.totalEarning || 0;
    const pendingAmount = metrics?.pendingAmount || 0;
    const avrgAmount = metrics?.averagePaymentPerDay || 0;
    const numberOfPayouts = metrics?.numberOfPayouts || 0;

    const eligibleTransactions = useMemo(() => {
        const list = Array.isArray(transactions) ? transactions : [];
        return list.filter((p) => isEligibleVisitStatus(p?.status));
    }, [transactions]);

    const filteredPayouts = useMemo(() => {
        let list = [...eligibleTransactions];

        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(
                (p) =>
                    (p.jobID || "").toLowerCase().includes(q) ||
                    (p.patientName || "").toLowerCase().includes(q) ||
                    (p.specialistName || "").toLowerCase().includes(q)
            );
        }

        if (statusFilter !== "all") {
            list = list.filter((p) => {
                if (statusFilter === "on-going") return isOngoingStatus(p.status);
                if (statusFilter === "completed") return isCompletedStatus(p.status);
                return true;
            });
        }

        if (timeFilter !== "all") {
            const today = moment().startOf("day");

            list = list.filter((p) => {
                const dates = (p.dutyTransactions || [])
                    .map((dt) =>
                        dt.date ? moment(dt.date, "YYYY-MM-DD").startOf("day") : null
                    )
                    .filter((d) => d && d.isValid());

                if (!dates.length) return false;

                if (timeFilter === "today") return dates.some((d) => d.isSame(today, "day"));

                let from;
                if (timeFilter === "last2") from = today.clone().subtract(1, "day");
                else if (timeFilter === "last7") from = today.clone().subtract(6, "day");
                else if (timeFilter === "last30") from = today.clone().subtract(29, "day");
                else return true;

                return dates.some((d) => d.isBetween(from, today, "day", "[]"));
            });
        }

        return list;
    }, [eligibleTransactions, search, statusFilter, timeFilter]);

    const totalVisitsEligible = eligibleTransactions.length;

    const handleDownloadStatement = async () => {
        const today = moment().startOf("day");
        let from = null;
        let to = null;

        if (statementType === "last10") {
            from = today.clone().subtract(9, "days").startOf("day");
            to = today.clone().endOf("day");
        } else if (statementType === "last1m") {
            from = today.clone().subtract(1, "month").startOf("day");
            to = today.clone().endOf("day");
        } else if (statementType === "last3m") {
            from = today.clone().subtract(3, "months").startOf("day");
            to = today.clone().endOf("day");
        } else if (statementType === "last6m") {
            from = today.clone().subtract(6, "months").startOf("day");
            to = today.clone().endOf("day");
        } else if (statementType === "custom") {
            if (!customFrom || !customTo) {
                alert("Please select both From and To dates.");
                return;
            }
            from = moment(customFrom, "YYYY-MM-DD").startOf("day");
            to = moment(customTo, "YYYY-MM-DD").endOf("day");
            if (!from.isValid() || !to.isValid()) {
                alert("Invalid custom date range.");
                return;
            }
            if (from.isAfter(to, "day")) {
                alert("From date cannot be after To date.");
                return;
            }
        } else if (statementType !== "overall") return;

        try {
            setIsGenerating(true);

            const result = await getRazorpayTransferStatement({
                partnerID: partnerID || partnerInfo?._id,
                fromDate: from?.format("YYYY-MM-DD"),
                toDate: to?.format("YYYY-MM-DD"),
            });

            console.log("result", result);

            if (result?.error !== false || !result?.data) {
                throw new Error(result?.message || "Failed to fetch Razorpay transfers");
            }
            if (!Array.isArray(result.data.transfers) || !result.data.transfers.length) {
                alert("No Razorpay transfers found in the selected period.");
                return;
            }

            const partnerName =
                partnerInfo?.basicInfo?.name ||
                partnerInfo?.name ||
                partnerInfo?.partnerName ||
                result.data.partnerName;
            await downloadTransferStatementExcel({
                report: { ...result.data, partnerName },
                from,
                to,
            });
            setShowStatementModal(false);
        } catch (err) {
            console.error("Error generating transfer statement:", err);
            alert(err.message || "Failed to generate statement. Please try again.");
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="bg-[#f3f5fb] rounded-[12px] w-full h-[90vh] flex flex-col gap-4 overflow-hidden p-2 relative">
            {(loading || isGenerating) && (
                <div>
                    <Loader loading={loading || isGenerating} />
                </div>
            )}

            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-lg font-semibold text-slate-900 font-poppins">
                        Track and manage
                    </h1>
                </div>

                <button
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-200 bg-white text-[12px] text-slate-700 hover:bg-slate-50"
                    onClick={() => setShowStatementModal(true)}
                    disabled={loading}
                >
                    <FiDownloadCloud size={14} />
                    <span className="font-poppins">Download Statement</span>
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <SummaryCard
                    title="Total Earnings"
                    amount={totalEarnings}
                    subtitle=""
                    icon={<FaRupeeSign />}
                    accent="bg-indigo-50 text-indigo-600"
                />

                <SummaryCard
                    title="Pending Amount"
                    amount={pendingAmount}
                    subtitle=""
                    icon={<MdAccessTime />}
                    accent="bg-amber-50 text-amber-500"
                />

                <SummaryCard
                    title="Average Payment per day"
                    amount={avrgAmount}
                    sub={numberOfPayouts}
                    subtitle=""
                    icon={<PiShoppingBag />}
                    accent="bg-emerald-50 text-emerald-500"
                />
            </div>

            <div className="flex items-center justify-between mt-1">
                <p className="text-[13px] font-semibold text-slate-800 font-poppins">
                    Patient Transaction History
                </p>
                <p className="text-[11px] text-slate-500 font-poppins">
                    {totalVisitsEligible} visits
                </p>
            </div>

            <div className="flex flex-col md:flex-row gap-3 items-center">
                <div className="relative flex-1 w-full">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[13px]">
                        <img
                            src={topBarImages.search}
                            className="h-4 w-4"
                            alt="Search Icon"
                        />
                    </span>

                    <input
                        type="text"
                        placeholder="Search by Ceviliyar ID, Patient Name, HCP Name or Category..."
                        className="w-full rounded-[12px] border border-slate-200 bg-white pl-8 pr-3 py-2 text-[12px] font-poppins outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        disabled={loading}
                    />
                </div>

                <select
                    className="w-full md:w-44 rounded-[12px] border border-slate-200 bg-white px-3 py-2 text-[12px] font-poppins outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    disabled={loading}
                >
                    <option value="all">All</option>
                    <option value="on-going">On-Going</option>
                    <option value="completed">Completed</option>
                </select>

                <select
                    className="w-full md:w-40 rounded-[12px] border border-slate-200 bg-white px-3 py-2 text-[12px] font-poppins outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                    value={timeFilter}
                    onChange={(e) => setTimeFilter(e.target.value)}
                    disabled={loading}
                >
                    <option value="today">Today</option>
                    <option value="last2">Last 2 Days</option>
                    <option value="last7">Last 7 Days</option>
                    <option value="last30">Last 30 Days</option>
                    <option value="all">Overall</option>
                </select>
            </div>

            <div className="flex-1 overflow-y-auto mt-2 space-y-3 pr-1">
                {filteredPayouts.map((payout) => {
                    const key = payout?.visitID || payout?.visitId || payout?.jobID;
                    return (
                        <PayoutCard
                            key={key}
                            payout={payout}
                            expanded={expandedId === key}
                            onToggle={() =>
                                setExpandedId((prev) => (prev === key ? null : key))
                            }
                            fetchBreakdown={fetchPayoutBreakdownCached}
                        />
                    );
                })}

                {filteredPayouts.length === 0 && !loading && (
                    <p className="text-center text-[12px] text-slate-500 mt-4 font-poppins">
                        No payouts found (On-Going / Completed).
                    </p>
                )}
            </div>

            {showStatementModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9998]">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-[15px] font-semibold font-poppins text-slate-900">
                                Download Payout Statement
                            </h3>
                            <button
                                onClick={() => setShowStatementModal(false)}
                                className="p-1 rounded-full hover:bg-slate-100"
                            >
                                <svg
                                    className="w-4 h-4 text-slate-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-3 text-[12px] font-poppins">
                            <div>
                                <label className="block text-slate-700 mb-1">Select Period</label>
                                <select
                                    className="w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-[12px] outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                                    value={statementType}
                                    onChange={(e) => setStatementType(e.target.value)}
                                >
                                    <option value="last10">Last 10 Days</option>
                                    <option value="last1m">Last 1 Month</option>
                                    <option value="last3m">Last 3 Months</option>
                                    <option value="last6m">Last 6 Months</option>
                                    <option value="overall">Overall</option>
                                    <option value="custom">Custom (From / To)</option>
                                </select>
                            </div>

                            {statementType === "custom" && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-slate-700 mb-1">From Date</label>
                                        <input
                                            type="date"
                                            className="w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-[12px] outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                                            value={customFrom}
                                            onChange={(e) => setCustomFrom(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-slate-700 mb-1">To Date</label>
                                        <input
                                            type="date"
                                            className="w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-[12px] outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                                            value={customTo}
                                            onChange={(e) => setCustomTo(e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}

                            <p className="text-[11px] text-slate-500 mt-1">
                                Statement includes Razorpay transfers for this partner's linked account
                                within the selected period.
                            </p>
                        </div>

                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                onClick={() => setShowStatementModal(false)}
                                className="px-3 py-1.5 rounded-md text-[12px] font-poppins bg-slate-100 text-slate-700 hover:bg-slate-200"
                                disabled={isGenerating}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDownloadStatement}
                                disabled={isGenerating}
                                className="px-3 py-1.5 rounded-md text-[12px] font-poppins bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-400 disabled:cursor-not-allowed"
                            >
                                {isGenerating ? "Generating..." : "Download Excel"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Payouts;
